/**
 * The contract between Sharetribe Console and the blog.
 *
 * Publishing an article must not require a deployment. That means the frontend
 * cannot know which articles exist — it has to ask. Sharetribe's asset store is
 * the source of truth, and this module is the single place that knows how a
 * Console page maps onto a public blog URL.
 *
 * The mapping is a naming convention, because Sharetribe page ids cannot
 * contain slashes and therefore cannot express `blog/my-article` directly:
 *
 *     Console page id   blog-winter-tire-rules
 *     Asset path        content/pages/blog-winter-tire-rules.json
 *     Public URL        /blog/winter-tire-rules
 *
 * The `blog-` prefix does double duty. It names the article, and it is what
 * separates blog content from ordinary `/p/` pages when the asset store is
 * enumerated — there is no other way to tell them apart, since the store is a
 * flat directory.
 *
 * Everything here is pure. Fetching lives in the ducks; this module only
 * translates, so both the browser and the sitemap generator can share one
 * definition of what a blog page is without dragging an SDK along.
 */

/** Marks a Console page as a blog article. Changing this changes every URL. */
export const BLOG_PAGE_PREFIX = 'blog-';

/** Where Sharetribe keeps Console pages. Trailing slash matters to queryAssets. */
export const PAGE_ASSET_PREFIX = 'content/pages/';

/**
 * Pages that live at fixed routes and are not articles, even though they sit in
 * the same directory. Listed so enumeration cannot accidentally publish one as
 * a blog post if somebody ever renames it.
 */
const RESERVED_PAGE_IDS = ['landing-page', 'terms-of-service', 'privacy-policy'];

/**
 * A slug is what appears in the URL. Kept deliberately strict: lowercase
 * letters, digits and single hyphens. Anything else is refused rather than
 * escaped, because a slug that needs escaping makes an ugly permanent URL and
 * the operator should learn that at authoring time.
 */
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const isValidSlug = slug =>
  typeof slug === 'string' && slug.length > 0 && slug.length <= 120 && SLUG_PATTERN.test(slug);

/** @returns {Boolean} whether this Console page id denotes a blog article */
export const isBlogPageId = pageId => {
  if (typeof pageId !== 'string' || !pageId.startsWith(BLOG_PAGE_PREFIX)) {
    return false;
  }
  if (RESERVED_PAGE_IDS.includes(pageId)) {
    return false;
  }
  return isValidSlug(pageId.slice(BLOG_PAGE_PREFIX.length));
};

/**
 * @param {String} pageId Console page id, e.g. "blog-winter-tire-rules"
 * @returns {String|null} the public slug, or null if this is not an article
 */
export const slugFromPageId = pageId =>
  isBlogPageId(pageId) ? pageId.slice(BLOG_PAGE_PREFIX.length) : null;

/**
 * @param {String} slug public slug, e.g. "winter-tire-rules"
 * @returns {String|null} the Console page id, or null if the slug is unusable
 */
export const pageIdFromSlug = slug => (isValidSlug(slug) ? `${BLOG_PAGE_PREFIX}${slug}` : null);

/** @returns {String|null} asset path to fetch for this slug */
export const assetPathForSlug = slug => {
  const pageId = pageIdFromSlug(slug);
  return pageId ? `${PAGE_ASSET_PREFIX}${pageId}.json` : null;
};

/** The one canonical public path for an article. */
export const blogPathForSlug = slug => `/blog/${slug}`;

/**
 * Turns an asset path from the asset store back into a page id.
 *
 * `queryAssets` returns paths that may or may not carry a leading slash
 * depending on how the prefix was given, so both are accepted.
 *
 * @param {String} assetPath e.g. "/content/pages/blog-example.json"
 * @returns {String|null} page id, or null if the path is not a Console page
 */
export const pageIdFromAssetPath = assetPath => {
  if (typeof assetPath !== 'string') {
    return null;
  }
  const normalised = assetPath.startsWith('/') ? assetPath.slice(1) : assetPath;
  if (!normalised.startsWith(PAGE_ASSET_PREFIX) || !normalised.endsWith('.json')) {
    return null;
  }
  return normalised.slice(PAGE_ASSET_PREFIX.length, -'.json'.length);
};

/**
 * Every blog slug present in an asset-store listing, in stable order.
 *
 * Anything that is not a well-formed blog page is dropped silently rather than
 * throwing: one malformed page in Console must not take the whole blog index
 * down. Duplicates are collapsed, since a slug is a URL and two of them cannot
 * both be canonical.
 *
 * @param {Array} assets `queryAssets` response data
 * @returns {Array<String>} slugs
 */
export const blogSlugsFromAssets = assets => {
  if (!Array.isArray(assets)) {
    return [];
  }
  const slugs = assets.reduce((acc, asset) => {
    const pageId = pageIdFromAssetPath(asset?.attributes?.assetPath);
    const slug = pageId ? slugFromPageId(pageId) : null;
    return slug ? [...acc, slug] : acc;
  }, []);

  return [...new Set(slugs)];
};

/**
 * The section an operator uses to declare article facts that Sharetribe pages
 * have no native field for — publication date, author and topic.
 *
 * It is a section rather than a naming trick because Console gives an operator
 * no other writable surface, and it is stripped before rendering so it never
 * appears on the page. Absent or partial is fine: a missing date is reported as
 * missing, never invented. A fabricated `dateModified` is worse than none —
 * Google only honours it when it is verifiably accurate.
 */
export const ARTICLE_META_SECTION_ID = 'article-meta';

const BLOCK_IDS = {
  datePublished: 'date-published',
  dateModified: 'date-modified',
  author: 'author',
  category: 'category',
  imageAlt: 'image-alt',
};

/** ISO-8601 date, date-only or with a time part. Anything else is not a date. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:T[\d:.]+(?:Z|[+-]\d{2}:\d{2})?)?$/;

const blockText = block => {
  const content = block?.text?.content;
  return typeof content === 'string' ? content.trim() : null;
};

/**
 * Reads the article-meta section, if the operator wrote one.
 *
 * @param {Object} pageAsset the Console page asset
 * @returns {Object} `{ datePublished, dateModified, author, category, imageAlt }`,
 *   each null when absent or unparseable
 */
export const readArticleMeta = pageAsset => {
  const sections = Array.isArray(pageAsset?.sections) ? pageAsset.sections : [];
  const metaSection = sections.find(s => s?.sectionId === ARTICLE_META_SECTION_ID);
  const blocks = Array.isArray(metaSection?.blocks) ? metaSection.blocks : [];

  const valueOf = blockId => blockText(blocks.find(b => b?.blockId === blockId));
  const dateOf = blockId => {
    const value = valueOf(blockId);
    return value && ISO_DATE.test(value) ? value : null;
  };

  return {
    datePublished: dateOf(BLOCK_IDS.datePublished),
    dateModified: dateOf(BLOCK_IDS.dateModified),
    author: valueOf(BLOCK_IDS.author),
    category: valueOf(BLOCK_IDS.category),
    imageAlt: valueOf(BLOCK_IDS.imageAlt),
  };
};

/**
 * The page as it should be rendered: the same asset with the meta section
 * removed, so declaring a publication date does not put a stray block of text
 * on the article.
 */
export const stripArticleMetaSection = pageAsset => {
  if (!pageAsset || !Array.isArray(pageAsset.sections)) {
    return pageAsset;
  }
  return {
    ...pageAsset,
    sections: pageAsset.sections.filter(s => s?.sectionId !== ARTICLE_META_SECTION_ID),
  };
};

/**
 * Everything the index card and the article head need, read from one asset.
 *
 * Title, description and hero come from the fields Console already provides for
 * SEO and social sharing, so an operator who fills in the SEO tab has by that
 * act also filled in the blog card. Nothing here is required: a page with no
 * social image still lists, without a picture.
 *
 * @param {Object} pageAsset the Console page asset (`{ sections, meta }`)
 * @param {String} slug the article's public slug
 * @returns {Object} article summary
 */
export const articleSummaryFromAsset = (pageAsset, slug) => {
  const meta = pageAsset?.meta || {};
  const social = meta.socialSharing || {};
  const image = social.images1200?.[0] || social.images600?.[0] || null;
  const articleMeta = readArticleMeta(pageAsset);

  return {
    slug,
    path: blogPathForSlug(slug),
    title: meta.pageTitle?.content || null,
    excerpt: meta.pageDescription?.content || null,
    imageUrl: image?.url || null,
    imageWidth: image?.width || null,
    imageHeight: image?.height || null,
    // Declared alt text wins; the headline is a serviceable fallback for a
    // hero image but a poor one for a chart or a photograph of a document.
    imageAlt: articleMeta.imageAlt || meta.pageTitle?.content || '',
    ...articleMeta,
  };
};

/**
 * Index order: newest first where a date is known, and pages without one after
 * those, alphabetically. Undated articles sort last rather than being hidden —
 * an operator who forgets the meta section should see their post appear and
 * notice it is at the bottom, not conclude that publishing is broken.
 */
export const sortArticles = articles =>
  [...articles].sort((a, b) => {
    const aDate = a.datePublished;
    const bDate = b.datePublished;
    if (aDate && bDate) return bDate.localeCompare(aDate);
    if (aDate) return -1;
    if (bDate) return 1;
    return a.slug.localeCompare(b.slug);
  });
